import React, { useCallback, useEffect, useRef, useState } from "react"
import { Modal, Button, Flex } from "antd"
import ReactCrop, { centerCrop, makeAspectCrop, convertToPixelCrop } from "react-image-crop"
import "react-image-crop/dist/ReactCrop.css"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import styles from "./index.module.css"

function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
  // Choose the largest crop that fits the image while maintaining aspect.
  // If the image is wider than target aspect, height will hit 100% and width < 100%.
  // If the image is narrower than target aspect, width will hit 100% and height < 100%.
  const imageAspect = mediaWidth / mediaHeight
  const widthPercent = Math.min(100, (aspect / imageAspect) * 100)
  return centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: widthPercent
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  )
}

const ImageCropper = ({ visible, onClose, imageSrc, onCropComplete, targetWidth = 1080, targetHeight = 1080, aspect, lockSize = true }) => {
  const [imageLoading, setImageLoading] = useState(true)
  const { t } = useTranslation()
  const [crop, setCrop] = useState()
  const [completedCrop, setCompletedCrop] = useState(null)
  const [imageRef, setImageRef] = useState(null)
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 })
  const [cropDimensions, setCropDimensions] = useState({ width: 0, height: 0 })
  const [isCropping, setIsCropping] = useState(false)
  const imgRef = useRef(null)
  const baseCropRef = useRef(null)

  // Reset crop state when image source changes or modal is opened/closed
  useEffect(() => {
    if (visible && imageSrc) {
      // Only reset if we have a new image or the modal was closed and reopened
      setCrop(undefined)
      setCompletedCrop(null)
      setImageDimensions({ width: 0, height: 0 })
    }
  }, [visible, imageSrc])

  const onLoad = useCallback(
    (img) => {
      if (!img) return

      imgRef.current = img
      setImageRef(img)
      setImageLoading(false)

      const { naturalWidth: width, naturalHeight: height } = img
      setImageDimensions({ width, height })

      // Calculate initial crop (supports both fixed-aspect and freeform)
      if (aspect) {
        const newCrop = centerAspectCrop(width, height, aspect)
        baseCropRef.current = newCrop
        setCrop(newCrop)
        setCompletedCrop(convertToPixelCrop(newCrop, width, height))
      } else {
        // Freeform: create a large centered crop without locking aspect
        const percentWidth = 90
        const percentHeight = Math.min(90, (percentWidth * height) / width) // keep within bounds
        const newCrop = centerCrop(
          {
            unit: "%",
            width: percentWidth,
            height: percentHeight
          },
          width,
          height
        )
        baseCropRef.current = newCrop
        setCrop(newCrop)
        setCompletedCrop(convertToPixelCrop(newCrop, width, height))
      }
    },
    [aspect]
  )

  const handleCropComplete = (crop) => {
    setCompletedCrop(crop)
    // Update cropDimensions on crop complete
    if (imageRef && crop?.width && crop?.height) {
      // crop is percent, convert to px
      const pxCrop = convertToPixelCrop(crop, imageRef.naturalWidth, imageRef.naturalHeight)
      setCropDimensions({ width: Math.round(pxCrop.width), height: Math.round(pxCrop.height) })
    }
  }

  const handleCrop = () => {
    if (!imageRef || !completedCrop) return
    setIsCropping(true)

    // Calculate crop dimensions in original image coordinates
    const scaleX = imageRef.naturalWidth / imageRef.width
    const scaleY = imageRef.naturalHeight / imageRef.height
    const cropX = Math.round(completedCrop.x * scaleX)
    const cropY = Math.round(completedCrop.y * scaleY)
    const cropWidth = Math.round(completedCrop.width * scaleX)
    const cropHeight = Math.round(completedCrop.height * scaleY)

    // Prepare crop info for backend
    const cropInfo = {
      x: cropX,
      y: cropY,
      width: cropWidth,
      height: cropHeight,
      originalWidth: imageRef.naturalWidth,
      originalHeight: imageRef.naturalHeight
    }

    // Optionally, create a preview blob (for UI, not backend)
    const canvas = document.createElement("canvas")
    canvas.width = cropWidth
    canvas.height = cropHeight
    const ctx = canvas.getContext("2d")
    ctx.drawImage(imageRef, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight)
    canvas.toBlob(
      (blob) => {
        if (blob) {
          // Send both blob (for preview) and cropInfo (for backend)
          onCropComplete(blob, cropInfo)
        }
        setIsCropping(false)
      },
      "image/jpeg",
      0.9
    )
  }

  if (!imageSrc) return null

  // Update cropDimensions live as user changes crop
  const handleCropChange = (c) => {
    // If locking size, keep width/height fixed to initial crop, only update x/y (clamped)
    if (lockSize && baseCropRef.current) {
      const base = baseCropRef.current
      const maxX = Math.max(0, 100 - (base.width || 0))
      const maxY = Math.max(0, 100 - (base.height || 0))
      const next = {
        unit: base.unit || "%",
        x: Math.max(0, Math.min(c.x ?? 0, maxX)),
        y: Math.max(0, Math.min(c.y ?? 0, maxY)),
        width: base.width,
        height: base.height
      }
      setCrop(next)
      if (imageRef && next.width && next.height) {
        const pxCrop = convertToPixelCrop(next, imageRef.naturalWidth, imageRef.naturalHeight)
        setCropDimensions({ width: Math.round(pxCrop.width), height: Math.round(pxCrop.height) })
      }
      return
    }

    // Default behavior (resizable)
    setCrop(c)
    if (imageRef && c.width && c.height) {
      const pxCrop = convertToPixelCrop(c, imageRef.naturalWidth, imageRef.naturalHeight)
      setCropDimensions({ width: Math.round(pxCrop.width), height: Math.round(pxCrop.height) })
    }
  }

  return (
    <Modal
      title={t("listings.tabs.media.edit_image_crop")}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={900}
      className={styles.imageCropperModal}
    >
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.helperText}>{t("listings.tabs.media.drag_to_resize_and_reposition")}</div>
          <Flex justify="center" align="center" className={styles.canvasWrap}>
            <Flex vertical className={styles.cropCard}>
              {/* Loader overlay while image is loading */}
              {imageLoading && (
                <div className={styles.loaderOverlay}>
                  <span className="ant-spin ant-spin-spinning" style={{ fontSize: 32 }} />
                </div>
              )}
              <ReactCrop
                crop={crop}
                onChange={handleCropChange}
                onComplete={handleCropComplete}
                aspect={aspect}
                className={styles.reactCrop}
                keepSelection={true}
                minWidth={100}
                minHeight={typeof aspect === "number" && !Number.isNaN(aspect) && aspect > 0 ? Math.round(100 * (1 / aspect)) : 50}
              >
                <div className={styles.cropArea}>
                  <img
                    ref={imgRef}
                    src={imageSrc}
                    crossOrigin="anonymous"
                    onLoad={(e) => {
                      onLoad(e.currentTarget)
                    }}
                    alt="Crop preview"
                    className={styles.image}
                  />
                </div>
              </ReactCrop>
              {/* Resolution display */}
              <div className={styles.resolution}>
                {imageDimensions.width} × {imageDimensions.height}
                {cropDimensions.width > 0 && cropDimensions.height > 0 && (
                  <>
                    {" "}
                    → {cropDimensions.width} × {cropDimensions.height}
                  </>
                )}
              </div>
            </Flex>
          </Flex>
        </div>
        {/* Footer Actions */}
        <div className={styles.footer}>
          <Button key="cancel" onClick={onClose}>
            {t("global.cancel")}
          </Button>
          <Button key="crop" type="primary" onClick={handleCrop} loading={isCropping} disabled={!completedCrop}>
            {t("global.update")}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

ImageCropper.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  imageSrc: PropTypes.string.isRequired,
  onCropComplete: PropTypes.func.isRequired,
  targetWidth: PropTypes.number,
  targetHeight: PropTypes.number,
  aspect: PropTypes.number,
  lockSize: PropTypes.bool
}

export default ImageCropper
